// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import userService from "../api/userService";
import axios from "../api/axiosConfig";
import { clearSession, readTokenExp, refreshSession, storeSession } from "../api/session";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

const PREVIEW_KEY = "trazia-privilege-preview";

function readPreview() {
  try {
    const raw = localStorage.getItem(PREVIEW_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storePreview(user) {
  try {
    if (user) localStorage.setItem(PREVIEW_KEY, JSON.stringify(user));
    else localStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [previewUser, setPreviewUser] = useState(() => readPreview());
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);

  const effectiveUser =
    user?.account === "admin" && previewUser ? previewUser : user;

  // === Cargar perfil al montar si hay token ===
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem("token");
      const storedSession = localStorage.getItem("sessionId");
      if (storedSession) setSessionId(storedSession);

      if (!token) {
        setLoading(false);
        return;
      }

      // Setear header para axios
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      try {
        const res = await userService.getProfile();
        const userData = res.data?.user ?? res.data.data;
        setUser(userData);
        if (userData?.account !== "admin") {
          storePreview(null);
          setPreviewUser(null);
        }
      } catch (error) {
        console.error("Error loading user:", error);
        logout(); // limpiar estado y token si hay error
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // === Login ===
  const login = async (email, password) => {
    const res = await userService.login({ email, password });
    const token = res.data?.accessToken ?? res.data?.token;
    const userData = res.data?.user ?? res.data.data;
    const session = res.data?.sessionId;

    if (!token) throw new Error("No se recibió token del login");

    storeSession({
      token,
      refreshToken: res.data?.refreshToken,
      sessionId: session,
    });
    if (session) setSessionId(session);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  // === Logout ===
  const logout = async () => {
    // Intentar cerrar la sesión en el backend
    const session = localStorage.getItem("sessionId");
    if (session) {
      try {
        await axios.post(`/audit/sessions/${session}/end`);
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
      }
    }
    
    clearSession();
    storePreview(null);
    localStorage.removeItem("trazia-admin-session");
    delete axios.defaults.headers.common.Authorization;
    setUser(null);
    setPreviewUser(null);
    setSessionId(null);
    document.documentElement.dataset.theme = "light";
  };

  const setPrivilegePreview = useCallback((nextUser) => {
    if (user?.account !== "admin") return;
    storePreview(nextUser);
    setPreviewUser(nextUser);
  }, [user?.account]);

  const clearPrivilegePreview = useCallback(() => {
    storePreview(null);
    setPreviewUser(null);
  }, []);

  const dropSession = () => {
    localStorage.removeItem("sessionId");
    setSessionId(null);
  };

  const updateUser = useCallback((partial) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const applyAppearance = useCallback((appearance) => {
    const next = appearance === "dark" ? "dark" : "light";
    const onLogin = window.location.pathname.startsWith("/login");
    document.documentElement.dataset.theme = onLogin ? "light" : next;
    try {
      localStorage.setItem("trazia-theme", next);
    } catch {
      /* ignore */
    }
  }, []);

  const setAppearance = useCallback(async (appearance) => {
    const next = appearance === "dark" ? "dark" : "light";
    applyAppearance(next);
    updateUser({ appearance: next });
    try {
      await userService.setAppearance(next);
    } catch (error) {
      console.error("No se pudo guardar el tema:", error);
    }
  }, [applyAppearance, updateUser]);

  useLayoutEffect(() => {
    if (user?.appearance) applyAppearance(user.appearance);
  }, [user?.appearance, applyAppearance]);

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!user) return undefined;
    let timer;
    let cancelled = false;

    const arm = () => {
      const exp = readTokenExp(localStorage.getItem("token"));
      const delay = Math.max(5000, (exp || Date.now() + 60_000) - Date.now() - 60_000);
      timer = setTimeout(async () => {
        try {
          const token = await refreshSession();
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;
          if (!cancelled) arm();
        } catch (_) {
          if (!cancelled) logoutRef.current();
        }
      }, delay);
    };

    arm();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [user]);

  const value = {
    user,
    previewUser,
    effectiveUser,
    isPreviewing: Boolean(previewUser && user?.account === "admin"),
    sessionId,
    isAuthenticated: Boolean(user),
    account: user?.account ?? null,
    login,
    logout,
    dropSession,
    updateUser,
    setAppearance,
    setPrivilegePreview,
    clearPrivilegePreview,
    loading,
  };

  // 🔹 Mostrar loader mientras se valida token
  if (loading) return <div>Cargando...</div>;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
